import { useCallback, useMemo, useState, useEffect } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import PlayCircleOutlineIcon from "@mui/icons-material/PlayCircleOutline";
import StopCircleOutlinedIcon from "@mui/icons-material/StopCircleOutlined";
import PersonOffIcon from "@mui/icons-material/PersonOff";
import RefreshIcon from "@mui/icons-material/Refresh";
import { useTranslation } from "react-i18next";
import SidebarLayout from "../components/sidebarLayout";
import SpaTreatmentCompletionDialog from "../components/SpaTreatmentCompletionDialog";
import {
  checkSpaAppointmentAvailability,
  checkInSpaAppointment,
  createSpaCabin,
  dateTimeWithLocalOffset,
  getSpaCabins,
  getSpaCancellationFee,
  getSpaPlanner,
  localDateTimeWithOffset,
  nowWithLocalOffset,
  postSpaCancellationFee,
  saveSpaTreatmentExecution,
  SPA_STATUSES,
} from "../APIs/SpaAPI";
import { getAllTavoli } from "../APIs/TablesAPI";
import { usePermissions } from "../contexts/PermissionsContext";
import { useSnackbar } from "../contexts/SnackBarContext";
import { localizedErrorMessage } from "../utils/italianErrorMessage";

const todayKey = () => {
  const date = new Date();
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
};

const statusByValue = new Map(SPA_STATUSES.map((status) => [status.value, status]));

const actionReason = "Marked from SPA daily operations";
const emptyFeeDialog = {
  open: false,
  appointment: null,
  preview: null,
  reason: actionReason,
  finalAmount: "",
  adjustmentReason: "",
  loading: false,
  saving: false,
  error: "",
};

const money = (value) => `EUR ${Number(value || 0).toFixed(2)}`;

const feeAmount = (preview) => Number(
  preview?.calculatedFee
  ?? preview?.calculatedAmount
  ?? preview?.feeAmount
  ?? preview?.amount
  ?? 0
);

const mapTableToSpaCabin = (table) => ({
  id: Number(table.id),
  roomId: Number(table.roomId || table.room?.id || table.id),
  tableId: Number(table.id),
  name: table.name || `Cabin ${table.id}`,
  capacity: Number(table.maxSeats || table.capacity || 1),
  active: table.active !== false,
  source: "table-map",
});

const isAreaResource = (resource) => /^area\s*\d+$/i.test(String(resource?.name || "").trim());

const finishTimeAfterStart = (appointment) => {
  const now = new Date();
  const actualStartAt = appointment?.execution?.actualStartAt
    ? new Date(appointment.execution.actualStartAt)
    : null;
  if (!actualStartAt || Number.isNaN(actualStartAt.getTime()) || now > actualStartAt) {
    return localDateTimeWithOffset(now);
  }
  return localDateTimeWithOffset(new Date(actualStartAt.getTime() + 1000));
};

function statusChip(status, isDark, t) {
  const config = statusByValue.get(status) || statusByValue.get("SCHEDULED");
  return (
    <Chip
      size="small"
      label={t(`spa.statuses.${status}`, { defaultValue: config?.label || status })}
      sx={{
        color: isDark ? "#fff" : config?.color,
        backgroundColor: isDark ? alpha(config?.color || "#64748B", 0.25) : config?.background,
        border: `1px solid ${alpha(config?.color || "#64748B", 0.35)}`,
        fontWeight: 800,
      }}
    />
  );
}

function SpaOperationsPage() {
  const { t } = useTranslation();
  const theme = useTheme();
  const { can } = usePermissions();
  const { showSnackbar } = useSnackbar();
  const isDark = theme.palette.mode === "dark";
  const token = localStorage.getItem("accessToken");
  const tenant = localStorage.getItem("tenantId");
  const tenantType = String(localStorage.getItem("tenantType") || "").toUpperCase();
  const canManageAppointments = can("spa-appointments:manage");
  const canManageClinical = can("spa-clinical:manage");
  const canRecordNoShow = canManageAppointments && can('spa-finance:view') && can('spa-finance:manage');
  const canViewProducts = can('spa-resources:view');
  const canViewCabins = can('spa-resources:view') || can('spa-finance:view');
  const canViewTables = can('tables:view');
  const [appointments, setAppointments] = useState([]);
  const [cabins, setCabins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [workingId, setWorkingId] = useState(null);
  const [error, setError] = useState("");
  const [feeDialog, setFeeDialog] = useState(emptyFeeDialog);
  const [completionAppointment, setCompletionAppointment] = useState(null);

  const loadOperations = useCallback(async () => {
    if (!token || !tenant) {
      setAppointments([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError("");
    try {
      const [plannerData, cabinData, tableData] = await Promise.all([
        getSpaPlanner({ date: todayKey(), view: "day" }, token, tenant),
        canViewCabins ? getSpaCabins(token, tenant).catch(() => []) : Promise.resolve([]),
        canViewTables ? getAllTavoli(token, tenant, { noCache: true }).catch(() => []) : Promise.resolve([]),
      ]);
      setAppointments(plannerData.appointments || []);
      const mappedTables = Array.isArray(tableData)
        ? tableData.map(mapTableToSpaCabin).filter((resource) => resource.id && !isAreaResource(resource))
        : [];
      const spaCabins = Array.isArray(cabinData)
        ? cabinData.filter((resource) => resource.id && !isAreaResource(resource))
        : [];
      setCabins([
        ...mappedTables,
        ...spaCabins,
      ]);
    } catch (err) {
      const message = localizedErrorMessage(
        err,
        t("spa.operations.loadError", { defaultValue: "Errore nel caricamento delle operazioni SPA" }),
      );
      setError(message);
      showSnackbar(message, "error");
    } finally {
      setLoading(false);
    }
  }, [showSnackbar, t, tenant, token, canViewCabins, canViewTables]);

  useEffect(() => {
    loadOperations();
  }, [loadOperations]);

  const metrics = useMemo(() => {
    const count = (value) => appointments.filter((item) => item.operationalStatus === value).length;
    return {
      scheduled: count("SCHEDULED"),
      arrived: count("ARRIVED"),
      waiting: count("WAITING"),
      inProgress: count("IN_PROGRESS"),
      completed: count("COMPLETED"),
      noShow: appointments.filter((item) => item.commercialStatus === "NO_SHOW").length,
    };
  }, [appointments]);

  const runAction = async (appointment, action) => {
    setWorkingId(appointment.id);
    try {
      await action();
      await loadOperations();
      window.dispatchEvent(new Event("map-data-changed"));
      showSnackbar(t("spa.operations.actionSaved", { defaultValue: "Operation updated" }), "success");
    } catch (err) {
      showSnackbar(err?.message || t("spa.operations.actionError", { defaultValue: "Error updating operation" }), "error");
    } finally {
      setWorkingId(null);
    }
  };

  const validAssignedCabins = (appointment) => (
    Array.isArray(appointment?.assignedResources?.cabins)
      ? appointment.assignedResources.cabins.filter((item) => item?.name && !isAreaResource(item))
      : []
  );

  const hasOnlyAreaCabinAssignment = (appointment) => {
    const rawAssignedCabins = Array.isArray(appointment?.assignedResources?.cabins)
      ? appointment.assignedResources.cabins
      : [];
    return rawAssignedCabins.length > 0 && validAssignedCabins(appointment).length === 0;
  };

  const assignedCabinIds = (appointment) => {
    const rawAssignedCabins = Array.isArray(appointment?.assignedResources?.cabins)
      ? appointment.assignedResources.cabins
      : [];
    if (rawAssignedCabins.length && !validAssignedCabins(appointment).length) return [];
    return (appointment.resourceCombination?.cabinIds || []).map(Number).filter(Boolean);
  };

  const assignedOperatorIds = (appointment) => (
    appointment.resourceCombination?.operatorIds || []
  ).map(Number).filter(Boolean);

  const hasCabinAssignment = (appointment) => assignedCabinIds(appointment).length > 0;

  const dedupeResourceCandidates = (items) => {
    const seen = new Set();
    return items.filter((item) => {
      const operatorIds = (item.operatorIds || []).map(Number).filter(Boolean);
      const cabinIds = (item.cabinIds || []).map(Number).filter(Boolean);
      if (!operatorIds.length || !cabinIds.length) return false;
      const key = `${operatorIds.join(",")}|${cabinIds.join(",")}`;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  };

  const sameCabinName = (left, right) => (
    String(left || "").trim().toLowerCase() === String(right || "").trim().toLowerCase()
  );

  const ensureCandidateCabins = async (resources) => {
    const cabinsToEnsure = Array.isArray(resources.cabinsToEnsure) ? resources.cabinsToEnsure : [];
    const ensuredCabinIds = [];
    for (const cabin of cabinsToEnsure) {
      const existing = cabins.find((item) => item.source !== "table-map" && sameCabinName(item.name, cabin.name));
      if (existing?.id) {
        ensuredCabinIds.push(Number(existing.id));
        continue;
      }
      if (!can('spa-resources:manage')) throw new Error(t('spa.operations.cabinSetupRequired'));
      try {
        const created = await createSpaCabin({
          name: cabin.name,
          cabinType: "MASSAGE",
          capacity: Math.max(1, Number(cabin.capacity || 1)),
          characteristics: [],
        }, token, tenant);
        if (created?.id) ensuredCabinIds.push(Number(created.id));
      } catch (err) {
        if (!String(err?.message || "").includes("already has a cabin profile")) {
          throw err;
        }
      }
    }
    return ensuredCabinIds.length ? { ...resources, cabinIds: ensuredCabinIds } : resources;
  };

  const resourceCandidates = async (appointment) => {
    const existingOperatorIds = assignedOperatorIds(appointment);
    const existingCabinIds = assignedCabinIds(appointment);
    if (existingOperatorIds.length && existingCabinIds.length) {
      return [{ operatorIds: existingOperatorIds, cabinIds: existingCabinIds }];
    }

    const preferredOperatorIds = existingOperatorIds.length
      ? existingOperatorIds
      : [Number(appointment.operatorId)].filter(Boolean);
    const preferredCabinIds = existingCabinIds.length
      ? existingCabinIds
      : hasOnlyAreaCabinAssignment(appointment) ? [] : [Number(appointment.resourceId)].filter(Boolean);

    const candidates = [];
    if (preferredOperatorIds.length && preferredCabinIds.length) {
      candidates.push({ operatorIds: preferredOperatorIds, cabinIds: preferredCabinIds });
    }

    try {
      const availability = await checkSpaAppointmentAvailability({
        treatmentProductId: Number(appointment.treatmentId),
        addonIds: (appointment.addonIds || []).map(Number).filter(Boolean),
        startAt: dateTimeWithLocalOffset(appointment.date, appointment.startTime),
        partySize: Number(appointment.partySize || 1),
        preferredResources: {
          operatorIds: preferredOperatorIds,
          cabinIds: preferredCabinIds,
          equipmentIds: [],
        },
      }, token, tenant);

      const combinations = Array.isArray(availability?.combinations) ? availability.combinations : [];
      combinations.forEach((combination) => {
        candidates.push({
          operatorIds: (combination.operatorIds || preferredOperatorIds).map(Number).filter(Boolean),
          cabinIds: (combination.cabinIds || preferredCabinIds).map(Number).filter(Boolean),
        });
      });
    } catch {
      // The check-in endpoint below is the final source of truth for conflicts.
    }

    cabins.forEach((cabin) => {
      candidates.push({
        operatorIds: preferredOperatorIds,
        cabinIds: [Number(cabin.id ?? cabin.roomId)].filter(Boolean),
        cabinsToEnsure: cabin.source === "table-map" ? [cabin] : [],
      });
    });

    const resolved = dedupeResourceCandidates(candidates);
    if (!resolved.length) {
      throw new Error(t('spa.operations.cabinRequired'));
    }
    return resolved;
  };

  const markCheckIn = (appointment, status) => runAction(appointment, async () => {
    const candidates = await resourceCandidates(appointment);
    let lastError = null;
    for (const resources of candidates) {
      try {
        const ensuredResources = await ensureCandidateCabins(resources);
        return await checkInSpaAppointment(appointment.id, {
          status,
          operatorIds: ensuredResources.operatorIds,
          cabinIds: ensuredResources.cabinIds,
        }, token, tenant);
      } catch (err) {
        lastError = err;
      }
    }
    throw lastError || new Error(t('spa.operations.cabinRequired'));
  });

  const startTreatment = (appointment) => runAction(appointment, async () => {
    const candidates = await resourceCandidates(appointment);
    const responsibleOperatorId = Number(appointment.operatorId || candidates[0]?.operatorIds?.[0]);
    if (!responsibleOperatorId) throw new Error(t('spa.operations.operatorRequired'));
    if (!hasCabinAssignment(appointment)) {
      let assigned = false;
      let lastError = null;
      for (const resources of candidates) {
        try {
          const ensuredResources = await ensureCandidateCabins(resources);
          await checkInSpaAppointment(appointment.id, {
            status: appointment.operationalStatus || "WAITING",
            operatorIds: ensuredResources.operatorIds,
            cabinIds: ensuredResources.cabinIds,
          }, token, tenant);
          assigned = true;
          break;
        } catch (err) {
          lastError = err;
        }
      }
      if (!assigned) throw lastError || new Error(t('spa.operations.cabinRequired'));
    }
    return saveSpaTreatmentExecution(appointment.id, {
      actualTreatmentProductId: Number(appointment.treatmentId),
      responsibleOperatorId,
      actualStartAt: nowWithLocalOffset(),
    }, token, tenant);
  });

  const finishTreatment = (appointment) => {
    if (!appointment.operatorId && !appointment.execution?.responsibleOperatorId && !appointment.assignedResources?.operators?.[0]?.id) {
      showSnackbar(t('spa.operations.operatorRequired'), "error");
      return;
    }
    setCompletionAppointment(appointment);
  };

  const markNoShow = async (appointment) => {
    if (!canRecordNoShow) return;
    setWorkingId(appointment.id);
    setFeeDialog({ ...emptyFeeDialog, open: true, appointment, loading: true });
    try {
      const preview = await getSpaCancellationFee(appointment.id, "NO_SHOW", token, tenant);
      const amount = feeAmount(preview);
      setFeeDialog((current) => ({
        ...current,
        preview,
        finalAmount: amount ? String(amount.toFixed(2)) : "0",
        loading: false,
      }));
    } catch (err) {
      const message = err?.message || t("spa.cancellation.previewError", { defaultValue: "Error calculating no-show fee" });
      setFeeDialog((current) => ({ ...current, error: message, loading: false }));
      showSnackbar(message, "error");
    } finally {
      setWorkingId(null);
    }
  };

  const confirmNoShow = async () => {
    if (!canRecordNoShow || !feeDialog.appointment || !feeDialog.preview || !feeDialog.reason.trim()) return;
    const calculated = feeAmount(feeDialog.preview);
    const finalAmount = Number(feeDialog.finalAmount || 0);
    const adjusted = Math.abs(finalAmount - calculated) > 0.009;
    if (adjusted && !feeDialog.adjustmentReason.trim()) {
      setFeeDialog((current) => ({
        ...current,
        error: t("spa.cancellation.adjustmentReasonRequired", { defaultValue: "Adjustment reason is required when the fee changes." }),
      }));
      return;
    }
    setFeeDialog((current) => ({ ...current, saving: true, error: "" }));
    setWorkingId(feeDialog.appointment.id);
    try {
      await postSpaCancellationFee(feeDialog.appointment.id, {
        status: "NO_SHOW",
        reason: feeDialog.reason.trim(),
        ...(adjusted ? {
          finalAmount,
          adjustmentReason: feeDialog.adjustmentReason.trim(),
        } : {}),
      }, token, tenant);
      setFeeDialog(emptyFeeDialog);
      await loadOperations();
      window.dispatchEvent(new Event("map-data-changed"));
      showSnackbar(t("spa.operations.actionSaved", { defaultValue: "Operation updated" }), "success");
    } catch (err) {
      setFeeDialog((current) => ({
        ...current,
        error: err?.message || t("spa.operations.actionError", { defaultValue: "Error updating operation" }),
      }));
      showSnackbar(err?.message || t("spa.operations.actionError", { defaultValue: "Error updating operation" }), "error");
    } finally {
      setWorkingId(null);
      setFeeDialog((current) => ({ ...current, saving: false }));
    }
  };

  if (tenantType !== "SPA") {
    return (
      <SidebarLayout>
        <Box sx={{ p: 3 }}>
          <Alert severity="warning">{t("spa.common.spaOnly")}</Alert>
        </Box>
      </SidebarLayout>
    );
  }

  return (
    <SidebarLayout>
      <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 1280, mx: "auto" }}>
        <Stack spacing={2.5}>
          <Stack direction={{ xs: "column", md: "row" }} justifyContent="space-between" alignItems={{ xs: "stretch", md: "center" }} spacing={2}>
            <Box>
              <Typography variant="h4" fontWeight={800}>
                {t("spa.operations.title", { defaultValue: "Check-in & Treatments" })}
              </Typography>
              <Typography color="text.secondary">
                {t("spa.operations.subtitle", { defaultValue: "Daily check-in and treatment execution board." })}
              </Typography>
            </Box>
            <Button variant="outlined" startIcon={<RefreshIcon />} onClick={loadOperations} disabled={loading}>
              {t("spa.common.refresh", { defaultValue: "Refresh" })}
            </Button>
          </Stack>

          {error ? <Alert severity="error">{error}</Alert> : null}
          {!canManageClinical ? (
            <Alert severity="info">
              {t("spa.operations.clinicalPermissionHint", {
                defaultValue: "Start and finish require spa-clinical:manage. Check-in and no-show require spa-appointments:manage.",
              })}
            </Alert>
          ) : null}

          <Grid container spacing={2}>
            {[
              ["SCHEDULED", metrics.scheduled, "#455A64"],
              ["ARRIVED", metrics.arrived, "#6B4CA5"],
              ["WAITING", metrics.waiting, "#D47200"],
              ["IN_PROGRESS", metrics.inProgress, "#9A5B00"],
              ["COMPLETED", metrics.completed, "#166534"],
              ["NO_SHOW", metrics.noShow, "#991B1B"],
            ].map(([label, value, color]) => (
              <Grid item xs={6} md={2} key={label}>
                <Paper variant="outlined" sx={{ p: 1.5, borderLeft: `5px solid ${color}`, background: isDark ? alpha(color, 0.12) : "#fff" }}>
                  <Typography variant="caption" color="text.secondary" fontWeight={800}>{t(`spa.statuses.${label}`)}</Typography>
                  <Typography variant="h5" fontWeight={900}>{value}</Typography>
                </Paper>
              </Grid>
            ))}
          </Grid>

          {loading ? (
            <Paper sx={{ p: 4, display: "grid", placeItems: "center" }}>
              <Stack alignItems="center" spacing={2}>
                <CircularProgress />
                <Typography>{t("spa.operations.loading", { defaultValue: "Loading operations..." })}</Typography>
              </Stack>
            </Paper>
          ) : appointments.length === 0 ? (
            <Alert severity="info">{t("spa.operations.empty", { defaultValue: "No SPA appointments for today." })}</Alert>
          ) : (
            <Stack spacing={1.5}>
              {appointments.map((appointment) => {
                const operationalStatus = appointment.operationalStatus || "SCHEDULED";
                const disabled = workingId === appointment.id;
                const isFinal = ["CANCELLED", "NO_SHOW"].includes(appointment.commercialStatus) || operationalStatus === "COMPLETED";
                const assignedCabinNames = validAssignedCabins(appointment).map((item) => item.name).join(", ");
                const missingCabin = !hasCabinAssignment(appointment);
                return (
                  <Paper key={appointment.id} variant="outlined" sx={{ p: 1.75 }}>
                    <Grid container spacing={1.5} alignItems="center">
                      <Grid item xs={12} md={2}>
                        <Typography fontWeight={900}>{appointment.startTime} - {appointment.endTime}</Typography>
                        <Typography variant="caption" color="text.secondary">{t(`spa.customerTypes.${({ HOTEL_GUEST: 'hotelGuest', EXTERNAL_GUEST: 'external', MEMBER: 'member' })[appointment.customerType]}`, { defaultValue: appointment.customerType })}</Typography>
                      </Grid>
                      <Grid item xs={12} md={3}>
                        <Typography fontWeight={850}>{appointment.clientName}</Typography>
                        <Typography variant="body2" color="text.secondary">{appointment.treatmentName || "-"}</Typography>
                      </Grid>
                      <Grid item xs={12} md={2}>
                        <Typography variant="body2">{appointment.assignedResources?.operators?.map((item) => [item.name, item.surname].filter(Boolean).join(" ")).join(", ") || "-"}</Typography>
                        <Typography variant="caption" color="text.secondary">{t("spa.fields.operator")}</Typography>
                      </Grid>
                      <Grid item xs={12} md={2}>
                        <Typography variant="body2">
                          {assignedCabinNames || t("spa.operations.assignCabin", { defaultValue: "Assign cabin" })}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">{t("spa.fields.cabin")}</Typography>
                      </Grid>
                      <Grid item xs={12} md={3}>
                        <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap justifyContent={{ xs: "flex-start", md: "flex-end" }}>
                          {statusChip(operationalStatus, isDark, t)}
                          {statusChip(appointment.commercialStatus, isDark, t)}
                          {missingCabin ? (
                            <Chip
                              size="small"
                              color="warning"
                              variant="outlined"
                              label={t("spa.operations.missingCabin", { defaultValue: "Missing cabin" })}
                            />
                          ) : null}
                        </Stack>
                      </Grid>
                      <Grid item xs={12}>
                        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap justifyContent="flex-end">
                          <Button
                            size="small"
                            variant="outlined"
                            startIcon={<CheckCircleOutlineIcon />}
                            disabled={!canManageAppointments || disabled || isFinal || operationalStatus !== "SCHEDULED"}
                            onClick={() => markCheckIn(appointment, "ARRIVED")}
                          >
                            {t('spa.statuses.ARRIVED')}
                          </Button>
                          <Button
                            size="small"
                            variant="outlined"
                            disabled={!canManageAppointments || disabled || isFinal || !["SCHEDULED", "ARRIVED"].includes(operationalStatus)}
                            onClick={() => markCheckIn(appointment, "WAITING")}
                          >
                            {t('spa.statuses.WAITING')}
                          </Button>
                          <Button
                            size="small"
                            variant="outlined"
                            color="success"
                            startIcon={<PlayCircleOutlineIcon />}
                            disabled={!canManageClinical || (missingCabin && !canManageAppointments) || disabled || isFinal || !["ARRIVED", "WAITING"].includes(operationalStatus)}
                            onClick={() => startTreatment(appointment)}
                          >
                            {t('spa.operations.start')}
                          </Button>
                          <Button
                            size="small"
                            variant="outlined"
                            color="success"
                            startIcon={<StopCircleOutlinedIcon />}
                            disabled={!canManageClinical || disabled || isFinal || operationalStatus !== "IN_PROGRESS"}
                            onClick={() => finishTreatment(appointment)}
                          >
                            {t('spa.operations.finish')}
                          </Button>
                          <Tooltip title={!canRecordNoShow ? t('spa.operations.noShowPermission') : ''}>
                            <span>
                              <Button
                                size="small"
                                variant="outlined"
                                color="error"
                                startIcon={<PersonOffIcon />}
                                disabled={!canRecordNoShow || disabled || isFinal}
                                onClick={() => markNoShow(appointment)}
                              >
                                {t('spa.statuses.NO_SHOW')}
                              </Button>
                            </span>
                          </Tooltip>
                        </Stack>
                      </Grid>
                    </Grid>
                  </Paper>
                );
              })}
            </Stack>
          )}
        </Stack>

        <Dialog
          open={feeDialog.open}
          onClose={() => feeDialog.saving ? null : setFeeDialog(emptyFeeDialog)}
          fullWidth
          maxWidth="sm"
        >
          <DialogTitle>{t("spa.cancellation.noShowTitle", { defaultValue: "Confirm no-show fee" })}</DialogTitle>
          <DialogContent dividers>
            <Stack spacing={2} sx={{ pt: 1 }}>
              {feeDialog.loading ? (
                <Stack alignItems="center" spacing={1}>
                  <CircularProgress size={24} />
                  <Typography>{t("spa.cancellation.loading", { defaultValue: "Calculating fee..." })}</Typography>
                </Stack>
              ) : null}
              {feeDialog.error ? <Alert severity="warning">{feeDialog.error}</Alert> : null}
              {feeDialog.preview ? (
                <Paper variant="outlined" sx={{ p: 1.5 }}>
                  <Grid container spacing={1}>
                    <Grid item xs={6}>
                      <Typography variant="caption" color="text.secondary">{t("spa.cancellation.appointmentValue", { defaultValue: "Appointment value" })}</Typography>
                      <Typography fontWeight={900}>{money(feeDialog.preview.appointmentValue || feeDialog.preview.orderTotal)}</Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="caption" color="text.secondary">{t("spa.cancellation.calculatedFee", { defaultValue: "Calculated fee" })}</Typography>
                      <Typography fontWeight={900}>{money(feeAmount(feeDialog.preview))}</Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="caption" color="text.secondary">{t("spa.cancellation.noticeMinutes", { defaultValue: "Notice minutes" })}</Typography>
                      <Typography fontWeight={900}>{feeDialog.preview.noticeMinutes ?? "-"}</Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="caption" color="text.secondary">{t("spa.cancellation.appliedPercent", { defaultValue: "Applied percent" })}</Typography>
                      <Typography fontWeight={900}>{feeDialog.preview.appliedPercent ?? feeDialog.preview.feePercent ?? "-"}%</Typography>
                    </Grid>
                  </Grid>
                </Paper>
              ) : null}
              <TextField
                fullWidth
                required
                multiline
                minRows={2}
                label={t("spa.fields.reason", { defaultValue: "Reason" })}
                value={feeDialog.reason}
                onChange={(event) => setFeeDialog((current) => ({ ...current, reason: event.target.value }))}
              />
              <TextField
                fullWidth
                type="number"
                label={t("spa.cancellation.finalAmount", { defaultValue: "Final fee amount" })}
                value={feeDialog.finalAmount}
                onChange={(event) => setFeeDialog((current) => ({ ...current, finalAmount: event.target.value }))}
                inputProps={{ min: 0, step: "0.01" }}
              />
              <TextField
                fullWidth
                label={t("spa.cancellation.adjustmentReason", { defaultValue: "Adjustment reason" })}
                value={feeDialog.adjustmentReason}
                onChange={(event) => setFeeDialog((current) => ({ ...current, adjustmentReason: event.target.value }))}
                helperText={t("spa.cancellation.managerHint", { defaultValue: "Required when changing or waiving the calculated fee." })}
              />
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setFeeDialog(emptyFeeDialog)} disabled={feeDialog.saving}>
              {t("spa.common.cancel")}
            </Button>
            <Button
              variant="contained"
              color="error"
              onClick={confirmNoShow}
              disabled={!canRecordNoShow || !feeDialog.preview || feeDialog.loading || feeDialog.saving || !feeDialog.reason.trim()}
            >
              {feeDialog.saving ? t("spa.common.saving", { defaultValue: "Saving..." }) : t("spa.common.confirm", { defaultValue: "Confirm" })}
            </Button>
          </DialogActions>
        </Dialog>

        <SpaTreatmentCompletionDialog
          open={Boolean(completionAppointment)}
          appointment={completionAppointment}
          actualEndAt={completionAppointment ? finishTimeAfterStart(completionAppointment) : null}
          canViewProducts={canViewProducts}
          token={token}
          tenant={tenant}
          onClose={() => setCompletionAppointment(null)}
          onCompleted={async () => {
            await loadOperations();
            window.dispatchEvent(new Event("map-data-changed"));
            showSnackbar(t("spa.operations.actionSaved", { defaultValue: "Operation updated" }), "success");
          }}
        />
      </Box>
    </SidebarLayout>
  );
}

export default SpaOperationsPage;
